/**
 * Choosing a block's client (sections 8.2 and 9.4; ADR 0033): one field that
 * lists every client and narrows as you type (ClientCombobox), and for a call
 * from an unknown number, optionally remember the number for that client so
 * the next call from it matches.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { ApiError } from "../../api/client";
import type { ClientAssignment, ClientChoice } from "../../api/types";
import { useSession } from "../../app/session-context";
import { ClientCombobox } from "../../components/ui/ClientCombobox";
import type { BlockView } from "./model";

function refusal(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 409) {
      return error.detail === "that number is already another client's"
        ? "That number is already another client's. Assign the block without remembering it."
        : "This block can't change any more.";
    }
    if (error.status === 403) {
      return "You can only change your own blocks.";
    }
    return error.fieldErrors[0]?.message ?? error.detail ?? error.message;
  }
  return error instanceof Error ? error.message : "Something went wrong.";
}

export function AssignClient({
  block,
  onAssigned,
}: {
  block: BlockView;
  onAssigned: (result: ClientAssignment, client: ClientChoice) => void;
}) {
  const { api } = useSession();
  const queryClient = useQueryClient();
  const [clientId, setClientId] = useState("");
  const [remember, setRemember] = useState(false);
  const choices = useQuery({
    queryKey: ["client-choices"],
    queryFn: () => api.listClientChoices(),
    staleTime: 5 * 60_000,
    // Once, so a failure shows within a second or so.
    retry: 1,
  });
  const chosen = choices.data?.find((c) => c.id === clientId);
  const assign = useMutation({
    mutationFn: (client: ClientChoice) => api.assignClient(block.id, client.id, remember && block.number !== null),
    onSuccess: async (result, client) => {
      onAssigned(result, client);
      await queryClient.invalidateQueries({ queryKey: ["day"] });
      // The Log form's preview (title, notes, hours) is built from the block's
      // activities: ask for it again, or it shows the old block (#224).
      await queryClient.invalidateQueries({ queryKey: ["submission-preview"] });
    },
  });

  return (
    <form
      aria-label="Choose the client"
      className="flex flex-col gap-2.5"
      onSubmit={(event) => {
        event.preventDefault();
        if (chosen) {
          assign.mutate(chosen);
        }
      }}
    >
      <ClientCombobox
        label="Client"
        choices={choices.data}
        failed={choices.isError}
        value={clientId}
        onChange={setClientId}
      />
      {block.number !== null ? (
        <label className="flex items-center gap-2 text-[13px]">
          <input
            type="checkbox"
            checked={remember}
            onChange={(event) => {
              setRemember(event.target.checked);
            }}
            className="size-4 accent-brand"
          />
          {`Remember ${block.number} for ${chosen?.name ?? "this client"}`}
        </label>
      ) : null}
      {assign.isError ? (
        <p role="alert" className="text-[13px] text-status-needs-client-fg">
          {refusal(assign.error)}
        </p>
      ) : null}
      <button
        type="submit"
        disabled={chosen === undefined || assign.isPending}
        className="h-11 cursor-pointer rounded-pill bg-cta-bg px-5 text-[14px] font-bold text-cta-fg disabled:cursor-not-allowed disabled:opacity-50"
      >
        {assign.isPending ? "Assigning…" : chosen ? `Assign to ${chosen.name}` : "Assign client"}
      </button>
    </form>
  );
}
